/**
 * Trends — global activity + temporal patterns.
 *
 * Section order:
 *   1. Global Activity (world map + country table) — full-width.
 *   2. Hour-of-day heatmap — UTC, total + high-risk overlay.
 *   3. Day-of-week distribution — Mon..Sun.
 *   4. Top rising entities — ranked by Δ vs the equal-length previous
 *      window, with a link to filter Investigations.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { GlobalActivitySection } from "@/components/overview/GlobalActivitySection";
import { ScoreHistogramChart } from "@/components/trends/ScoreHistogramChart";
import { ReasonCooccurrenceMatrix } from "@/components/trends/ReasonCooccurrenceMatrix";
import { EndpointMatrixHeatmap } from "@/components/trends/EndpointMatrixHeatmap";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useUrlState } from "@/hooks/useUrlState";
import { pivotTo } from "@/lib/pivot";
import { fmtNumber, fmtSignedPct } from "@/lib/format";
import type { TrendsV1 } from "@/types/v1";

const DAY_LABELS = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function Trends() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();

  const q = useQuery({
    queryKey: ["trends", filters, tick],
    queryFn: ({ signal }) => api.trends(filters, signal),
  });

  return (
    <div className="flex flex-col gap-5">
      <GlobalActivitySection />

      {q.isLoading ? (
        <Skeleton className="h-96" />
      ) : q.error ? (
        <Card><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>
      ) : q.data?.data ? (
        <>
          <Card>
            <CardHeader title="Hour-of-day pattern (UTC)" subtitle="Total events per hour, with the high-risk share overlaid." />
            <HourHeatmap data={q.data.data.hourOfDay} />
          </Card>

          <Card>
            <CardHeader title="Day-of-week distribution" subtitle="Total events vs high-risk events, by weekday (UTC)." />
            <DayChart data={q.data.data.dayOfWeek} />
          </Card>

          <ScoreHistogramChart />
          <EndpointMatrixHeatmap />
          <ReasonCooccurrenceMatrix />

          <RisingSections rows={q.data.data.topRisingEntities} />
        </>
      ) : (
        <Card><EmptyState title="No trend data available." /></Card>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */

function HourHeatmap({ data }: { data: TrendsV1["hourOfDay"] }) {
  const max = useMemo(() => data.reduce((m, d) => Math.max(m, d.total), 0) || 1, [data]);
  const cells = Array.from({ length: 24 }, (_, h) => data.find((d) => d.hour === h) ?? { hour: h, total: 0, highRisk: 0 });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-24 gap-1" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
        {cells.map((c) => {
          // Square-root curve gives mid-range hours visible contrast even
          // when totals are bunched near the max.
          const t = Math.sqrt(c.total / max);
          const hShare = c.total ? c.highRisk / c.total : 0;
          return (
            <div
              key={c.hour}
              title={`${String(c.hour).padStart(2, "0")}:00 UTC — ${c.total.toLocaleString()} events (${c.highRisk.toLocaleString()} high-risk)`}
              className="relative aspect-square rounded"
              style={{
                backgroundColor: `rgb(var(--accent) / ${0.05 + t * 0.85})`,
                outline: hShare > 0.05 ? `1px solid rgb(var(--sev-high) / ${Math.min(1, hShare * 6)})` : "none",
                outlineOffset: "-1px",
              }}
            >
              <span className="absolute inset-0 grid place-items-center font-mono text-[10px] text-fg-muted">{c.hour}</span>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-fg-subtle">
        Read-only overview of when traffic and risk concentrate (UTC). Brighter = more events; red outline = high-risk share &gt; 5%. Hover any hour for exact counts.
      </p>
    </div>
  );
}

function DayChart({ data }: { data: TrendsV1["dayOfWeek"] }) {
  const rows = data.map((d) => ({ ...d, label: DAY_LABELS[d.day] ?? String(d.day) }));
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="rgb(var(--border))" strokeDasharray="2 4" />
          <XAxis dataKey="label" stroke="rgb(var(--fg-muted))" tick={{ fontSize: 11 }} />
          <YAxis stroke="rgb(var(--fg-muted))" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNumber(v, { compact: true })} />
          <Tooltip
            cursor={{ fill: "rgb(var(--surface-3))" }}
            contentStyle={{ background: "rgb(var(--surface-1))", border: "1px solid rgb(var(--border))", fontSize: 12 }}
          />
          <Bar dataKey="total" name="Total" fill="rgb(var(--accent))" radius={[4, 4, 0, 0]} />
          <Bar dataKey="highRisk" name="High risk" fill="rgb(var(--sev-high))" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RisingTable({ data, onPivot, entityLabel }: {
  data: TrendsV1["topRisingEntities"];
  onPivot: (row: TrendsV1["topRisingEntities"][number]) => void;
  entityLabel: string;
}) {
  if (!data.length) return <EmptyState title="No comparable previous window." />;
  return (
    <div className="overflow-hidden rounded border border-border">
      <table className="w-full text-sm">
        <thead className="bg-surface-3 text-[11px] uppercase tracking-wider text-fg-subtle">
          <tr>
            <th className="px-3 py-2 text-left font-medium">{entityLabel}</th>
            <th className="px-3 py-2 text-right font-medium">Events</th>
            <th className="px-3 py-2 text-right font-medium">Δ vs prev</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r) => {
            const tone = r.deltaPct == null ? "text-fg-subtle"
              : r.deltaPct > 0.5 ? "text-sev-high"
              : r.deltaPct > 0.1 ? "text-sev-watch"
              : r.deltaPct < -0.1 ? "text-sev-good"
              : "text-fg-muted";
            return (
              <tr
                key={r.value}
                onClick={() => onPivot(r)}
                title={`Open Investigations filtered to ${r.value}`}
                className="cursor-pointer border-t border-border hover:bg-surface-2 focus-within:bg-surface-2"
              >
                <td className="max-w-0 truncate px-3 py-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); onPivot(r); }}
                    className="block w-full truncate text-left font-mono text-xs text-fg hover:text-accent focus:outline-none"
                  >{r.value}</button>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs text-fg">{fmtNumber(r.events, { compact: true })}</td>
                <td className={"whitespace-nowrap px-3 py-2 text-right font-mono text-xs " + tone}>
                  {r.deltaPct == null ? "new" : fmtSignedPct(r.deltaPct * 100)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RisingSections({ rows }: { rows: TrendsV1["topRisingEntities"] }) {
  const [, patchUrl] = useUrlState();
  const networks = rows.filter((r) => r.type === "asn");
  const emails   = rows.filter((r) => r.type === "email");
  const businesses = rows.filter((r) => r.type === "business");

  const pivot = (row: TrendsV1["topRisingEntities"][number]) => {
    if (row.type === "asn") {
      const m = /^AS(\d+)$/.exec(row.value);
      if (!m) return;
      pivotTo(patchUrl, { asn: m[1] }, "investigations");
    } else if (row.type === "email") {
      pivotTo(patchUrl, { email: row.value }, "investigations");
    } else if (row.type === "business") {
      pivotTo(patchUrl, { businessId: row.value }, "investigations");
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader
          title="Top rising networks (ASNs)"
          subtitle="Networks with the biggest event-count jumps vs the previous equal-length window. Click a row to filter Investigations."
        />
        <RisingTable data={networks} onPivot={pivot} entityLabel="ASN" />
      </Card>

      <Card>
        <CardHeader
          title="Top rising customers (emails)"
          subtitle="Customer accounts with the biggest event-count jumps vs the previous equal-length window. Click a row to investigate."
        />
        <RisingTable data={emails} onPivot={pivot} entityLabel="Email" />
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader
          title="Top rising businesses"
          subtitle="Business accounts with the biggest event-count jumps vs the previous equal-length window. Click a row to investigate."
        />
        <RisingTable data={businesses} onPivot={pivot} entityLabel="Business ID" />
      </Card>
    </div>
  );
}
